import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, ChevronDown } from "lucide-react";
import { useState } from "react";
import { UI, PATHS } from "@/i18n/ui";
import { waLink } from "@/data/site";

export const Section = ({ children, className = "", "data-testid": testId }) => (
  <section className={`py-16 lg:py-24 ${className}`} data-testid={testId}>
    <div className="max-w-7xl mx-auto px-6 lg:px-8">{children}</div>
  </section>
);

const REVEAL_INITIAL = { opacity: 0, y: 24 };
const REVEAL_VISIBLE = { opacity: 1, y: 0 };
const REVEAL_VIEWPORT = { once: true, margin: "-60px" };

export const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={REVEAL_INITIAL}
    whileInView={REVEAL_VISIBLE}
    viewport={REVEAL_VIEWPORT}
    transition={{ duration: 0.55, delay }}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children }) => (
  <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-[#4A6B53] mb-4">{children}</p>
);

export const PageHead = ({ eyebrow, title, sub, lang }) => (
  <Section className="pb-8 lg:pb-12">
    <div className="max-w-3xl">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1F2937]">{title}</h1>
      {sub && <p className="mt-6 text-base md:text-lg leading-relaxed text-[#4B5563]">{sub}</p>}
    </div>
  </Section>
);

export const CtaBand = ({ lang, title, sub }) => {
  const t = UI[lang];
  return (
    <Section className="pb-24" data-testid="cta-band">
      <div className="rounded-3xl bg-[#4A6B53] px-8 py-14 lg:px-16 lg:py-20 text-center">
        <h2 className="text-3xl sm:text-4xl text-white tracking-tight">{title || t.sections.finalCta}</h2>
        <p className="mt-4 text-white/80 max-w-2xl mx-auto text-base md:text-lg">{sub || t.sections.finalCtaSub}</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to={PATHS[lang].booking} data-testid="cta-band-book"
            className="inline-flex items-center justify-center rounded-full bg-white text-[#4A6B53] font-semibold px-8 py-4 hover:bg-[#F2EFE9] transition-colors">
            {t.cta.book}
          </Link>
          <a href={waLink(lang === "id" ? "Halo Bali Empower, saya ingin berkonsultasi." : "Hello Bali Empower, I would like a consultation.")}
            target="_blank" rel="noopener noreferrer" data-testid="cta-band-whatsapp"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white font-semibold px-8 py-4 hover:bg-white/10 transition-colors">
            <MessageCircle size={18} /> {t.cta.whatsapp}
          </a>
        </div>
      </div>
    </Section>
  );
};

export const FaqList = ({ items, testPrefix = "faq" }) => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="divide-y divide-[#E5E1D8] border-y border-[#E5E1D8]">
      {items.map(([q, a], i) => (
        <div key={q}>
          <button onClick={() => setOpenIdx(openIdx === i ? null : i)}
            data-testid={`${testPrefix}-question-${i}`} aria-expanded={openIdx === i}
            className="w-full flex items-center justify-between gap-4 py-5 text-left">
            <span className="font-medium text-[#1F2937]">{q}</span>
            <ChevronDown size={18} className={`shrink-0 text-[#4A6B53] transition-transform ${openIdx === i ? "rotate-180" : ""}`} />
          </button>
          {openIdx === i && (
            <p className="pb-5 text-[#4B5563] leading-relaxed text-[15px]" data-testid={`${testPrefix}-answer-${i}`}>{a}</p>
          )}
        </div>
      ))}
    </div>
  );
};

export const QaBlock = ({ q, children }) => (
  <div className="py-6 border-b border-[#E5E1D8]">
    <h2 className="text-xl sm:text-2xl font-display text-[#1F2937] mb-3">{q}</h2>
    <div className="text-[#4B5563] leading-relaxed text-[15px] space-y-2">{children}</div>
  </div>
);
